import { MerchantNumberRule } from 'features/authorization/constants/merchantNumbers';
import { CarrierChannelSummaryType, CarrierReferenceItem } from 'features/authorization/helpers/carrierReference';
import { activeRulesFrom, toMerchantRule } from 'features/authorization/merchantRules/rules';
import { BaseCarrierRule } from 'features/authorization/merchantRules/types';

// ─── "Códigos de comercio y cuotas" con las reglas de la base ────────────────
// La página `/aerolineas/codigos-de-comercio-y-cuotas` muestra las reglas de
// `GET /rules` agrupadas por aerolínea, con los mismos componentes que la tabla
// fija del Power. La validación y el paso a la forma de la tabla fija son los
// del modal de autorización (`features/authorization/merchantRules`). Funciones puras.

type ChannelOfRule = Pick<BaseCarrierRule, 'cardBrand' | 'channel'>;

const isFiserv = (rule: ChannelOfRule) => rule.channel === 'fiserv';
const isCallCenter = (brand: string) => (rule: ChannelOfRule) =>
	rule.cardBrand === brand && rule.channel === 'call_center';
const isAmexCallCenter = isCallCenter('amex');
const isVisaCallCenter = isCallCenter('visa');

/**
 * El canal de la aerolínea (`CarrierChannelSummary`), de los `channel` de sus reglas:
 *  - todas `no_cards` → `no_cards`; todas `gds` → `gds`;
 *  - sin contar Amex por call center (el resumen ya dice que Amex va siempre al call center AX): todas `fiserv` →
 *    `fiserv_always`; `fiserv` y alguna Visa por call center, sin nada más → `fiserv_except_visa`;
 *  - si no, o sin reglas, `unknown`.
 * Con la semilla da lo mismo que las constantes de `authorizationChannels`.
 */
export function carrierChannelSummary(rules: readonly ChannelOfRule[]): CarrierChannelSummaryType {
	if (rules.length === 0) return 'unknown';
	if (rules.every((rule) => rule.channel === 'no_cards')) return 'no_cards';
	if (rules.every((rule) => rule.channel === 'gds')) return 'gds';
	const rest = rules.filter((rule) => !isAmexCallCenter(rule));
	if (!rest.some(isFiserv)) return 'unknown';
	if (rest.every(isFiserv)) return 'fiserv_always';
	return rest.every((rule) => isFiserv(rule) || isVisaCallCenter(rule)) ? 'fiserv_except_visa' : 'unknown';
}

/** Las filas que se ven, sin repetir: la semilla abre por sello lo que en la tabla fija es una fila (Copa, Lufthansa). */
function visibleRows(own: readonly BaseCarrierRule[]): MerchantNumberRule[] {
	const seen = new Set<string>();
	return own.map(toMerchantRule).filter((row) => {
		const key = JSON.stringify([row.cardLabel, row.merchantNumber, row.installmentsLabel, row.validUntil, row.notes]);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

/**
 * Las aerolíneas de unas reglas vigentes, por nombre como la tabla fija, cada una con sus filas (en el orden de carga,
 * que con la semilla es el de la tabla fija) y su canal.
 */
export function carriersOf(rules: readonly BaseCarrierRule[]): CarrierReferenceItem[] {
	const byCode = new Map<string, BaseCarrierRule[]>();
	rules.forEach((rule) => byCode.set(rule.carrierCode, [...(byCode.get(rule.carrierCode) ?? []), rule]));
	return Array.from(byCode.entries())
		.map(([code, own]) => ({
			code,
			name: own[0].carrierName,
			rules: visibleRows(own),
			summaryType: carrierChannelSummary(own),
		}))
		.sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

/** Las aerolíneas de lo que respondió `/rules` (`activeRulesFrom` y `carriersOf`); `null` si no se le puede creer. */
export function carriersFromRules(
	data: { rules?: unknown; pagination?: { total?: unknown } } | null | undefined,
): CarrierReferenceItem[] | null {
	const rules = activeRulesFrom(data);
	return rules && carriersOf(rules);
}
